// 補印單據勾選、記憶、送出參數與查詢協調的共用工具。
// 本檔刻意零 import，以便用 Vitest 直接測（見 .claude/rules/testing.md）。

/**
 * 補印單據的全部勾選項 key（與後端 printOrder 參數同名）
 * @type {string[]}
 */
export const PRINT_OPTION_KEYS = [
	'printStoreCopy',
	'printCustomerCopy',
	'printKitchenCopy',
	'printKitchenAreaA',
	'printKitchenAreaB',
	'printKitchenAreaC',
	'printTag',
]

/** 廚房分區的 key，勾選任一都代表要出廚房聯 */
const KITCHEN_AREA_KEYS = [
	'printKitchenAreaA',
	'printKitchenAreaB',
	'printKitchenAreaC',
]

/**
 * 建立一組全未勾選的補印選項
 * @returns {Object} 每個 key 皆為 false 的新物件
 */
export function createPrintOptions() {
	return PRINT_OPTION_KEYS.reduce((options, key) => {
		options[key] = false
		return options
	}, {})
}

/**
 * 還原上次的補印勾選。
 * 只套用「當前彈窗實際看得見」的項目，其餘一律 false ——
 * 避免使用者在畫面上看不到卻被偷偷帶入而誤印（例如報表頁沒有標籤貼紙、
 * 廚房分區開關切換後 A/B/C 與廚房聯互換、外送訂單不顯示廚房分區）。
 * @param {Object|null} saved 上次儲存的勾選（來源為持久化狀態，可能為 null 或格式異常）
 * @param {string[]} visibleKeys 當前彈窗顯示中的 key
 * @returns {Object} 可直接指派給 printOptions 的新物件
 */
export function restorePrintOptions(saved, visibleKeys) {
	const options = createPrintOptions()
	if (!saved || typeof saved !== 'object' || !Array.isArray(visibleKeys)) {
		return options
	}
	visibleKeys.forEach(key => {
		if (key in options && saved[key] === true) {
			options[key] = true
		}
	})
	return options
}

/**
 * 正規化勾選內容：勾了任一廚房分區就一併帶上廚房聯
 * @param {Object} options 使用者當前勾選
 * @returns {Object} 正規化後的新物件（不修改傳入物件）
 */
export function normalizePrintOptions(options) {
	const normalized = { ...createPrintOptions(), ...options }
	if (KITCHEN_AREA_KEYS.some(key => normalized[key])) {
		normalized.printKitchenCopy = true
	}
	return normalized
}

/**
 * 把本次送出的勾選寫回記憶：只覆蓋當前彈窗看得見的項目，看不見的沿用舊記憶。
 * 整包覆蓋會讓「這次看不到」的項目被記成 false —— 例如平常記著店家聯＋廚房聯，
 * 補印一張外送訂單（不顯示廚房聯）後，廚房聯的記憶就被清掉了。
 * @param {Object|null} saved 目前儲存的記憶（可能為 null 或格式異常）
 * @param {Object} submitted 本次送出的勾選
 * @param {string[]} visibleKeys 當前彈窗顯示中的 key
 * @returns {Object} 可直接寫回持久化狀態的新物件
 */
export function mergePrintOptions(saved, submitted, visibleKeys) {
	// 先把舊記憶正規化成完整結構，濾掉髒資料與未知 key
	const merged = restorePrintOptions(saved, PRINT_OPTION_KEYS)
	if (!Array.isArray(visibleKeys)) return merged
	visibleKeys.forEach(key => {
		if (key in merged) {
			merged[key] = submitted?.[key] === true
		}
	})
	return merged
}

/**
 * 是否至少勾選一項單據
 * @param {Object} options
 * @returns {boolean}
 */
export function hasAnyPrintSelection(options) {
	if (!options) return false
	return PRINT_OPTION_KEYS.some(key => options[key] === true)
}

export const PRINT_RECEIPT_TYPES = {
	printStoreCopy: 'STORE_COPY',
	printCustomerCopy: 'CUSTOMER_COPY',
	printKitchenCopy: 'KITCHEN_COPY',
	printKitchenAreaA: 'KITCHEN_AREA_A',
	printKitchenAreaB: 'KITCHEN_AREA_B',
	printKitchenAreaC: 'KITCHEN_AREA_C',
}

/** 建立開窗時已設定 MAC 且在線的紙本機器清單；名稱只供顯示。 */
export function getReprintPrinters(storage, order) {
	return [
		{
			machineType: 'POS',
			name: storage.posDeviceId,
			mac: storage.posMac,
			connected: order.BTPosPrinter,
		},
		{
			machineType: 'POS_SECOND',
			name: storage.posDeviceIdSecond,
			mac: storage.posMacSecond,
			connected: order.BTPosPrinterSecond,
		},
	]
		.filter(printer => storage.hasPos && printer.mac && printer.connected)
		.map(({ machineType, name }) => ({ machineType, name }))
}

/** zeroSettings 必須由後端先篩為所有已設定機器皆零張的聯別。 */
export function getForcePrintKeys(options, visibleKeys, zeroSettings) {
	const zeroTypes = new Set(zeroSettings.map(item => item.receiptType))
	return visibleKeys.filter(
		key => options[key] === true && zeroTypes.has(PRINT_RECEIPT_TYPES[key]),
	)
}

/** 先移除強制聯別，再為正常分區補上廚房旗標；不修改 UI 勾選。 */
export function buildReprintOptions(options, visibleKeys, forceTargets) {
	const normal = restorePrintOptions(options, visibleKeys)
	for (const [key, receiptType] of Object.entries(PRINT_RECEIPT_TYPES)) {
		if (forceTargets.some(target => target.receiptType === receiptType))
			normal[key] = false
	}
	return {
		...normalizePrintOptions(normal),
		printOrder: false,
		forcePrintTargets: forceTargets.map(target => ({ ...target })),
	}
}

/** 同一父頁面的補印查詢只保留最後一次操作，不管理已送出的列印。 */
export function createReprintQueryCoordinator() {
	let pending = null
	return {
		cancel() {
			const previous = pending
			pending = null
			previous?.()
		},
		start(cancel) {
			this.cancel()
			pending = cancel
			return () => {
				if (pending === cancel) pending = null
			}
		},
	}
}
